/**
 * Velocity stat constants and utilities for baseball and softball pitching statistics
 */

/**
 * Baseball velocity stat keys with their display names
 */
export const BASEBALL_VELOCITY_STATS = {
    MPHFB: 'Fastball Average Velocity',
    MPHCT: 'Cutter Average Velocity',
    MPHCB: 'Curveball Average Velocity',
    MPHSL: 'Slider Average Velocity',
    MPHCH: 'Changeup Average Velocity',
    MPHOS: 'Offspeed Average Velocity',
} as const;

/**
 * Softball velocity stat keys with their display names
 */
export const SOFTBALL_VELOCITY_STATS = {
    MPHRB: 'Riseball Average Velocity',
    MPHDB: 'Dropball Average Velocity',
    MPHSC: 'Screwball Average Velocity',
    MPHDC: 'Drop Curve Average Velocity',
    MPHKB: 'Knuckleball Average Velocity',
    MPHKC: 'Knuckle Curve Average Velocity',
} as const;

/**
 * All velocity stats combined
 */
export const ALL_VELOCITY_STATS = {
    ...BASEBALL_VELOCITY_STATS,
    ...SOFTBALL_VELOCITY_STATS,
} as const;

/**
 * Pitch type abbreviations for column headers
 */
const PITCH_TYPE_ABBREVIATIONS = {
    FB: 'FB Velo',
    CT: 'CT Velo',
    CB: 'CB Velo',
    SL: 'SL Velo',
    CH: 'CH Velo',
    OS: 'OS Velo',
    RB: 'RB Velo',
    DB: 'DB Velo',
    SC: 'SC Velo',
    DC: 'DC Velo',
    KB: 'KB Velo',
    KC: 'KC Velo',
} as const;

/**
 * Generate velocity stat abbreviations from pitch type codes
 */
const createVelocityAbbreviations = <T extends Record<string, string>>(stats: T) => {
    return Object.fromEntries(
        Object.keys(stats).map((key) => {
            const pitchType = key.replace('MPH', '') as keyof typeof PITCH_TYPE_ABBREVIATIONS;
            return [key, PITCH_TYPE_ABBREVIATIONS[pitchType] || `${pitchType} Velo`];
        }),
    ) as Record<keyof T, string>;
};

const BASEBALL_PITCH_TYPE_ABBREVIATIONS = createVelocityAbbreviations(BASEBALL_VELOCITY_STATS);
const SOFTBALL_PITCH_TYPE_ABBREVIATIONS = createVelocityAbbreviations(SOFTBALL_VELOCITY_STATS);

const ALL_PITCH_TYPE_ABBREVIATIONS = {
    ...BASEBALL_PITCH_TYPE_ABBREVIATIONS,
    ...SOFTBALL_PITCH_TYPE_ABBREVIATIONS,
} as const;

/**
 * Type definitions
 */
export type VelocityStatKey = keyof typeof ALL_VELOCITY_STATS;
export type BaseballVelocityStatKey = keyof typeof BASEBALL_VELOCITY_STATS;
export type SoftballVelocityStatKey = keyof typeof SOFTBALL_VELOCITY_STATS;

/**
 * Utility functions
 */

/**
 * Check if a stat key is a velocity stat
 */
export const isVelocityStat = (statKey: string): statKey is VelocityStatKey => {
    return statKey.startsWith('MPH') && statKey in ALL_VELOCITY_STATS;
};

/**
 * Check if a stat key is a baseball velocity stat
 */
export const isBaseballVelocityStat = (statKey: string): statKey is BaseballVelocityStatKey => {
    return statKey in BASEBALL_VELOCITY_STATS;
};

/**
 * Check if a stat key is a softball velocity stat
 */
export const isSoftballVelocityStat = (statKey: string): statKey is SoftballVelocityStatKey => {
    return statKey in SOFTBALL_VELOCITY_STATS;
};

/**
 * Get the display name for a velocity stat
 */
export const getVelocityStatDisplayName = (statKey: VelocityStatKey): string => {
    return ALL_VELOCITY_STATS[statKey];
};

/**
 * Get the abbreviated column header for a velocity stat
 */
export const getVelocityStatAbbreviation = (statKey: VelocityStatKey): string => {
    return ALL_PITCH_TYPE_ABBREVIATIONS[statKey as keyof typeof ALL_PITCH_TYPE_ABBREVIATIONS];
};

/**
 * Extract velocity stats from a stats object
 */
export const extractVelocityStats = (stats: Record<string, any>): Record<VelocityStatKey, any> => {
    const velocityStats: Partial<Record<VelocityStatKey, any>> = {};

    Object.keys(stats).forEach((key) => {
        if (isVelocityStat(key)) {
            velocityStats[key] = stats[key];
        }
    });

    return velocityStats as Record<VelocityStatKey, any>;
};

export const normalizeStatTabViewName = (viewName: string): string => {
    return viewName.replaceAll(' ', '_').toLowerCase();
};
